import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { 
  Building2, Check, ArrowRight, 
  Loader, Calendar, CreditCard,
  ShieldCheck, Award, BarChart, Users, 
  LogIn, UserPlus, Star, Briefcase, Home
} from 'lucide-react';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.6, 
      ease: "easeOut" 
    } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const Planes = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('mensual');
  const [userType, setUserType] = useState('vendedor'); // 'vendedor', 'inmobiliaria', 'constructora'
  const [plans, setPlans] = useState([]);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Verificar si el usuario está autenticado y redirigir si es necesario
  useEffect(() => {
    if (isAuthenticated) {
      // Si el usuario ya está autenticado, redirigir a la página de upgrade
      navigate('/upgrade-plan');
    } else {
      // Solo cargar planes si el usuario no está autenticado
      setTimeout(() => {
        setPlans(getPlansForUserType(userType));
        setLoading(false);
      }, 500);
    }
  }, [isAuthenticated, userType, navigate]);

  // Obtener planes según tipo de usuario seleccionado
  const getPlansForUserType = (type) => {
    switch (type) {
      case 'vendedor':
        return [
          {
            id: "1",
            name: 'Gratuito',
            codigo: 'gratuito',
            price: "0.00",
            properties: 1,
            color: 'from-gray-300 to-gray-400',
            textColor: 'text-gray-700 dark:text-gray-300',
            gradientButton: 'from-gray-500 to-gray-600',
            icon: CreditCard,
            benefits: ['Primera publicación gratis', 'Perfil básico', 'Estadísticas básicas', 'Sin permanencia'],
            featured: false
          },
          {
            id: "2",
            name: 'Casual',
            codigo: 'casual',
            price: "99.90",
            properties: 5,
            color: 'from-indigo-500 to-blue-600',
            textColor: 'text-gray-800 dark:text-gray-200',
            gradientButton: 'from-indigo-600 to-blue-600',
            icon: Calendar,
            benefits: ['Hasta 5 publicaciones', 'Perfil profesional', 'Estadísticas detalladas', 'Soporte prioritario', 'Herramientas de promoción'],
            featured: true
          },
          {
            id: "3",
            name: 'Socio Inmobiliaria',
            codigo: 'socio_inmobiliaria',
            price: "199.90",
            properties: 10,
            color: 'from-green-500 to-teal-600',
            textColor: 'text-gray-800 dark:text-gray-200',
            gradientButton: 'from-green-600 to-teal-600',
            icon: Building2,
            benefits: ['Hasta 10 publicaciones', 'Perfil empresarial', 'Panel avanzado', 'Destaca tus Propiedades', 'Soporte Premium'],
            featured: false
          }
        ];
      case 'inmobiliaria':
        return [
          {
            id: "4",
            name: 'Básico',
            codigo: 'basico',
            price: "199.90",
            properties: 10,
            color: 'from-green-500 to-teal-600',
            textColor: 'text-gray-800 dark:text-gray-200',
            gradientButton: 'from-green-600 to-teal-600',
            icon: ShieldCheck,
            benefits: ['Hasta 10 publicaciones', 'Perfil empresarial', 'Estadísticas básicas', 'Soporte por email', 'Acceso API básico'],
            featured: false
          },
          {
            id: "5",
            name: 'Estándar',
            codigo: 'estandar',
            price: "349.90",
            properties: 20,
            color: 'from-indigo-500 to-blue-600',
            textColor: 'text-gray-800 dark:text-gray-200',
            gradientButton: 'from-indigo-600 to-blue-600',
            icon: BarChart,
            benefits: ['Hasta 20 publicaciones', 'Perfil empresarial destacado', 'Estadísticas avanzadas', 'Soporte prioritario', 'Logo en propiedades', 'Reportes mensuales'],
            featured: true
          },
          {
            id: "6",
            name: 'Premium',
            codigo: 'premium',
            price: "499.90",
            properties: 'Ilimitadas',
            color: 'from-amber-500 to-yellow-500',
            textColor: 'text-gray-800 dark:text-gray-200',
            gradientButton: 'from-amber-600 to-yellow-600',
            icon: Award,
            benefits: ['Publicaciones ilimitadas', 'Perfil empresarial premium', 'Estadísticas en tiempo real', 'Soporte 24/7', 'Logo destacado', 'Integraciones avanzadas'],
            featured: false
          }
        ];
      case 'constructora':
        return [
          {
            id: "7",
            name: 'Premium Constructora',
            codigo: 'premium_constructora',
            price: "699.90",
            properties: 'Ilimitadas',
            color: 'from-purple-500 to-pink-500',
            textColor: 'text-gray-800 dark:text-gray-200',
            gradientButton: 'from-purple-600 to-pink-600',
            icon: Award,
            benefits: ['Publicaciones ilimitadas', 'Perfiles de desarrollos', 'Estadísticas por proyecto', 'Soporte dedicado', 'Branding personalizado', 'Herramientas de pre-venta'],
            featured: true
          }
        ];
      default:
        return [];
    }
  };

  // Calcular precio anual (10 meses) y ahorro (2 meses)
  const calculateAnnualPrice = (monthlyPrice) => {
    if (monthlyPrice === '0') return '0';
    const monthly = parseFloat(monthlyPrice);
    return (monthly * 10).toFixed(2);
  };

  const calculateSavings = (monthlyPrice) => {
    if (monthlyPrice === '0') return '0';
    const monthly = parseFloat(monthlyPrice);
    return (monthly * 2).toFixed(2);
  };

  // Manejar selección de plan
  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setShowActionModal(true);
  };

  // Manejar navegación después de seleccionar un plan
  const handleActionChoice = (action) => {
    setShowActionModal(false);
    
    // Guardar información del plan seleccionado
    localStorage.setItem('selectedPlanId', selectedPlan.id);
    localStorage.setItem('selectedBillingCycle', billingCycle);
    
    // Navegar a la página correspondiente
    navigate(`/${action}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-white dark:from-gray-900 dark:to-gray-800 pt-32">
        <div className="flex flex-col items-center">
          <motion.div
            className="w-16 h-16 border-4 border-indigo-200 dark:border-indigo-800 border-t-indigo-600 dark:border-t-indigo-400 rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
          <p className="mt-4 text-indigo-700 dark:text-indigo-400 font-medium">Cargando planes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white dark:from-gray-900 dark:to-gray-800 py-16 px-4">
      <motion.div 
        className="max-w-6xl mx-auto"
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
      >
        <motion.div variants={fadeInUp} className="text-center mb-8">
          <h1 className="text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-700 to-indigo-700 dark:from-blue-400 dark:to-indigo-400">
            Nuestros Planes
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Encuentra el plan perfecto para tus necesidades inmobiliarias
          </p>
        </motion.div>
        
        {/* Selector de tipo de usuario */}
        <motion.div variants={fadeInUp} className="flex justify-center mb-12">
          <div className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 p-3 rounded-xl shadow-md dark:shadow-black/50 inline-flex border border-white/50 dark:border-gray-700/50">
            <button
              onClick={() => setUserType('vendedor')}
              className={`px-5 py-3 rounded-lg transition-all duration-300 flex items-center gap-2 ${
                userType === 'vendedor'
                ? 'bg-gradient-to-r from-indigo-600 to-blue-600 dark:from-indigo-500 dark:to-blue-500 text-white shadow-md dark:shadow-black/25'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50'
              }`}
            >
              <Home className="w-5 h-5" />
              Vendedor Individual
            </button>
            <button
              onClick={() => setUserType('inmobiliaria')}
              className={`px-5 py-3 rounded-lg transition-all duration-300 flex items-center gap-2 ${
                userType === 'inmobiliaria'
                ? 'bg-gradient-to-r from-indigo-600 to-blue-600 dark:from-indigo-500 dark:to-blue-500 text-white shadow-md dark:shadow-black/25'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50'
              }`}
            >
              <Briefcase className="w-5 h-5" />
              Inmobiliaria
            </button>
            <button
              onClick={() => setUserType('constructora')}
              className={`px-5 py-3 rounded-lg transition-all duration-300 flex items-center gap-2 ${
                userType === 'constructora'
                ? 'bg-gradient-to-r from-indigo-600 to-blue-600 dark:from-indigo-500 dark:to-blue-500 text-white shadow-md dark:shadow-black/25'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50'
              }`}
            >
              <Building2 className="w-5 h-5" />
              Constructora
            </button>
          </div>
        </motion.div>

        {/* Billing cycle toggle */}
        <motion.div variants={fadeInUp} className="flex justify-center items-center mb-10">
          <div className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 p-2 rounded-xl shadow-md dark:shadow-black/50 border border-white/50 dark:border-gray-700/50">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setBillingCycle('mensual')}
                className={`px-6 py-2 rounded-lg transition-all duration-300 ${
                  billingCycle === 'mensual'
                    ? 'bg-gradient-to-r from-indigo-600 to-blue-600 dark:from-indigo-500 dark:to-blue-500 text-white shadow-md dark:shadow-black/25'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                }`}
              >
                Mensual
              </button>
              <button
                onClick={() => setBillingCycle('anual')}
                className={`px-6 py-2 rounded-lg transition-all duration-300 ${
                  billingCycle === 'anual'
                    ? 'bg-gradient-to-r from-indigo-600 to-blue-600 dark:from-indigo-500 dark:to-blue-500 text-white shadow-md dark:shadow-black/25'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                }`}
              >
                Anual (2 meses gratis)
              </button>
            </div>
          </div>
        </motion.div>

        {/* Cards de planes */}
        <motion.div 
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={staggerContainer}
        >
          {plans.map((plan) => {
            const Icon = plan.icon;
            const monthlyPrice = plan.price;
            const annualPrice = calculateAnnualPrice(monthlyPrice);
            const savingsAmount = calculateSavings(monthlyPrice);
            
            return (
              <motion.div 
                key={plan.id}
                variants={fadeInUp}
                whileHover={{ y: -8, transition: { duration: 0.2 } }}
                className={`relative backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 rounded-2xl shadow-xl dark:shadow-black/50 overflow-hidden 
                  transition-all duration-300 cursor-pointer border border-white/50 dark:border-gray-700/50
                  ${plan.featured ? 'lg:-mt-4 lg:mb-4 border-2 border-blue-500 dark:border-blue-400' : ''}
                `}
                onClick={() => handleSelectPlan(plan)}
              >
                {plan.featured && (
                  <div className="absolute top-4 right-4 z-10">
                    <span className="bg-gradient-to-r from-indigo-600 to-blue-600 dark:from-indigo-500 dark:to-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1 shadow-lg">
                      <Star className="w-3 h-3" /> Recomendado
                    </span>
                  </div>
                )}

                <div 
                  className={`absolute inset-0 bg-gradient-to-br ${plan.color} opacity-10 dark:opacity-20`}
                />

                <div className="relative p-8 z-10">
                  <div className="flex items-center mb-6">
                    <div className={`p-3 rounded-full bg-gradient-to-br ${plan.color} text-white mr-4 shadow-lg`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className={`text-2xl font-bold ${plan.textColor}`}>
                      {plan.name}
                    </h3>
                  </div>
                  
                  <div className="flex items-baseline mb-2">
                    <span className="text-4xl font-bold text-gray-800 dark:text-gray-200">
                      S/ {billingCycle === 'mensual' ? monthlyPrice : annualPrice}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 ml-2">
                      /{billingCycle === 'mensual' ? 'mes' : 'año'}
                    </span>
                  </div>
                  
                  {billingCycle === 'anual' && monthlyPrice !== '0' && (
                    <div className="mb-6 text-sm text-green-600 dark:text-green-400 font-medium">
                      Ahorras S/ {savingsAmount} al año
                    </div>
                  )}
                  {billingCycle === 'mensual' && (
                    <div className="mb-6 h-5"></div> // Espacio para mantener alineación
                  )}

                  <div className="border-t border-gray-100 dark:border-gray-700 pt-6 mb-8">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      {typeof plan.properties === 'number' 
                        ? `Hasta ${plan.properties} publicaciones`
                        : 'Publicaciones ilimitadas'}
                    </p>

                    <ul className="space-y-4">
                      {plan.benefits.map((benefit, index) => (
                        <li 
                          key={index} 
                          className="flex items-start gap-3 text-gray-700 dark:text-gray-300"
                        >
                          <div className="flex-shrink-0 rounded-full p-1 bg-green-100 dark:bg-green-900/30">
                            <Check className="w-4 h-4 text-green-500 dark:text-green-400" />
                          </div>
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className={`w-full py-3 px-4 rounded-xl font-medium flex items-center justify-center gap-2 shadow-md dark:shadow-black/25
                      bg-gradient-to-r ${plan.gradientButton} text-white hover:shadow-lg dark:hover:shadow-black/40 transition-all duration-300`}
                    onClick={() => handleSelectPlan(plan)}
                  >
                    Elegir Plan
                    <ArrowRight className="w-5 h-5" />
                  </motion.button>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Comparativa expandida para inmobiliarias */}
        {userType === 'inmobiliaria' && (
          <motion.div variants={fadeInUp} className="mt-16 backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 p-8 rounded-xl shadow-lg dark:shadow-black/50 border border-white/50 dark:border-gray-700/50">
            <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-6 text-center">Comparativa de Planes para Inmobiliarias</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-gray-800 dark:text-gray-200">Características</th>
                    <th className="px-4 py-3 text-center text-gray-800 dark:text-gray-200">Básico</th>
                    <th className="px-4 py-3 text-center bg-indigo-50 dark:bg-indigo-900/20 text-gray-800 dark:text-gray-200">Estándar</th>
                    <th className="px-4 py-3 text-center text-gray-800 dark:text-gray-200">Premium</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-gray-200 dark:border-gray-700">
                    <td className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Propiedades</td>
                    <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">10</td>
                    <td className="px-4 py-3 text-center bg-indigo-50 dark:bg-indigo-900/20 text-gray-600 dark:text-gray-400">20</td>
                    <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">Ilimitadas</td>
                  </tr>

                  <tr className="border-t border-gray-200 dark:border-gray-700">
                    <td className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Destacados</td>
                    <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">1 por mes</td>
                    <td className="px-4 py-3 text-center bg-indigo-50 dark:bg-indigo-900/20 text-gray-600 dark:text-gray-400">3 por mes</td>
                    <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">5 por mes</td>
                  </tr>
                  <tr className="border-t border-gray-200 dark:border-gray-700">
                    <td className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Logo en propiedades</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center">
                        <div className="w-5 h-5 text-red-500 dark:text-red-400">✕</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center bg-indigo-50 dark:bg-indigo-900/20">
                      <div className="flex justify-center">
                        <Check className="w-5 h-5 text-green-500 dark:text-green-400" />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center">
                        <Check className="w-5 h-5 text-green-500 dark:text-green-400" />
                      </div>
                    </td>
                  </tr>
                  <tr className="border-t border-gray-200 dark:border-gray-700">
                    <td className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">API</td>
                    <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">Básica</td>
                    <td className="px-4 py-3 text-center bg-indigo-50 dark:bg-indigo-900/20 text-gray-600 dark:text-gray-400">Completa</td>
                    <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">Premium</td>
                  </tr>
                  <tr className="border-t border-gray-200 dark:border-gray-700">
                    <td className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Reportes</td>
                    <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">Básicos</td>
                    <td className="px-4 py-3 text-center bg-indigo-50 dark:bg-indigo-900/20 text-gray-600 dark:text-gray-400">Avanzados</td>
                    <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">Personalizados</td>
                  </tr>
                  <tr className="border-t border-gray-200 dark:border-gray-700">
                    <td className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Soporte</td>
                    <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">Email</td>
                    <td className="px-4 py-3 text-center bg-indigo-50 dark:bg-indigo-900/20 text-gray-600 dark:text-gray-400">Prioritario</td>
                    <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">24/7</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* Información adicional y CTA */}
        <motion.div 
          variants={fadeInUp}
          className="max-w-3xl mx-auto mt-20 text-center"
        >
          <div className="flex items-center justify-center mb-6">
            <Users className="w-10 h-10 text-indigo-600 dark:text-indigo-400 mr-4" />
            <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200">¿Necesitas un plan personalizado?</h3>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mb-8">
            Si tienes necesidades específicas para tu negocio inmobiliario,
            contamos con planes personalizados. Contáctanos y te asesoramos.
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/contacto')}
            className="px-8 py-4 bg-gradient-to-r from-indigo-600 to-blue-600 dark:from-indigo-500 dark:to-blue-500 text-white rounded-xl shadow-lg dark:shadow-black/25 hover:shadow-xl dark:hover:shadow-black/40 transition-all duration-300"
          >
            Contáctanos
          </motion.button>
        </motion.div>

        {/* Preguntas frecuentes */}
        <motion.div variants={fadeInUp} className="mt-20">
          <h3 className="text-3xl font-bold text-center mb-8 text-gray-800 dark:text-gray-200">Preguntas Frecuentes</h3>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 p-6 rounded-xl shadow-md dark:shadow-black/50 border border-white/50 dark:border-gray-700/50">
              <h4 className="text-lg font-semibold mb-3 text-indigo-700 dark:text-indigo-400">¿Puedo cambiar de plan en cualquier momento?</h4>
              <p className="text-gray-600 dark:text-gray-400">Sí, puedes actualizar tu plan en cualquier momento. Si cambias a un plan superior, se aplicará un prorrateo del costo.</p>
            </div>
            <div className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 p-6 rounded-xl shadow-md dark:shadow-black/50 border border-white/50 dark:border-gray-700/50">
              <h4 className="text-lg font-semibold mb-3 text-indigo-700 dark:text-indigo-400">¿Hay período de permanencia?</h4>
              <p className="text-gray-600 dark:text-gray-400">No hay período de permanencia obligatorio. Puedes cancelar tu suscripción en cualquier momento.</p>
            </div>
            <div className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 p-6 rounded-xl shadow-md dark:shadow-black/50 border border-white/50 dark:border-gray-700/50">
              <h4 className="text-lg font-semibold mb-3 text-indigo-700 dark:text-indigo-400">¿Qué métodos de pago aceptan?</h4>
              <p className="text-gray-600 dark:text-gray-400">Aceptamos tarjetas de crédito/débito a través de PayU, transferencias bancarias y depósitos.</p>
            </div>
            <div className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 p-6 rounded-xl shadow-md dark:shadow-black/50 border border-white/50 dark:border-gray-700/50">
              <h4 className="text-lg font-semibold mb-3 text-indigo-700 dark:text-indigo-400">¿El precio incluye impuestos?</h4>
              <p className="text-gray-600 dark:text-gray-400">Sí, todos nuestros precios ya incluyen IGV y están expresados en Soles (S/).</p>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Modal de acciones (login/registro) */}
      {showActionModal && (
        <div className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div 
            className="backdrop-blur-xl bg-white/95 dark:bg-gray-800/95 rounded-xl p-6 shadow-2xl dark:shadow-black/50 max-w-md w-full border border-white/50 dark:border-gray-700/50"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200">¡Excelente elección!</h3>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Has seleccionado el plan <span className="font-semibold">{selectedPlan?.name}</span>
              </p>
            </div>

            <div className="space-y-4">
              <button
                onClick={() => handleActionChoice('login')}
                className="w-full py-3 px-4 bg-indigo-600 dark:bg-indigo-700 text-white rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-indigo-700 dark:hover:bg-indigo-600 transition shadow-lg dark:shadow-black/25"
              >
                <LogIn className="w-5 h-5" />
                Ya tengo cuenta - Iniciar sesión
              </button>
              
              <button
                onClick={() => handleActionChoice('login')}
                className="w-full py-3 px-4 bg-green-600 dark:bg-green-700 text-white rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-green-700 dark:hover:bg-green-600 transition shadow-lg dark:shadow-black/25"
              >
                <UserPlus className="w-5 h-5" />
                Crear nueva cuenta
              </button>
              
              <button
                onClick={() => setShowActionModal(false)}
                className="w-full py-3 px-4 bg-gray-100 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition"
              >
                Seguir explorando planes
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Planes;